// Customers — list view backed by /admin/customers. Mirrors the
// Bookings/Cars/Vendors pattern: server-side filter + search,
// keepPreviousData on the query, mutation error banner reserved for
// future lock/unlock buttons.

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import {
  Avatar,
  PageHeader,
  ToolBtn,
  ToolChip,
  ToolSearch,
  Toolbar,
} from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import { env } from '@/lib/env';
import {
  downloadCustomersCsv,
  fetchAdminCustomers,
  type AdminCustomerRow,
  type AdminCustomersListParams,
} from '@/lib/admin-api';
import { usePager } from '@/lib/use-pager';

function resolveAvatarUrl(rel: string | null | undefined): string | null {
  if (!rel) return null;
  if (/^https?:\/\//.test(rel)) return rel;
  return `${env().PAYLOAD_URL}${rel}`;
}

type FilterKey = NonNullable<AdminCustomersListParams['status']>;

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'pending', label: 'Pending' },
  { key: 'suspended', label: 'Suspended' },
];

const fmtMoney = (v: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(v);

const COLUMNS: DataTableColumn<AdminCustomerRow>[] = [
  {
    key: 'name',
    h: 'Customer',
    cell: (r) => (
      <div className="flex items-center gap-2">
        <Avatar name={r.displayName} size={26} src={resolveAvatarUrl(r.avatarUrl)} />
        <div className="min-w-0">
          <div className="truncate font-medium">{r.displayName}</div>
          <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">{r.email}</div>
        </div>
      </div>
    ),
  },
  { key: 'phone', h: 'Phone', cell: (r) => r.phone ?? '—' },
  {
    key: 'bookings',
    h: 'Bookings',
    right: true,
    cell: (r) => <span className="tabular-nums">{r.bookingsTotal}</span>,
  },
  {
    key: 'lifetime',
    h: 'Lifetime spend',
    right: true,
    cell: (r) => <span className="tabular-nums">{fmtMoney(r.lifetimeValue)}</span>,
  },
  {
    key: 'verified',
    h: 'Verified',
    cell: (r) =>
      r.verified ? (
        <span className="text-[12px] text-[#166534]">Yes</span>
      ) : (
        <span className="text-[12px] text-[color:var(--bo-mute)]">No</span>
      ),
  },
  { key: 'status', h: 'Status', cell: (r) => <StatusPill status={r.status} /> },
];

export function Customers() {
  const [filter, setFilter] = useState<FilterKey>('all');
  const [q, setQ] = useState('');
  const { page, setPage, limit, setLimit } = usePager();

  // Any filter change lands back on page 1.
  useEffect(() => {
    setPage(1);
  }, [filter, q, setPage]);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadCustomersCsv({ status: filter, q: q.trim() || undefined });
    } catch (err) {
      setExportError((err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'customers', { filter, q, page }],
    queryFn: ({ signal }) =>
      fetchAdminCustomers(
        { status: filter, q: q.trim() || undefined, page, limit },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const navigate = useNavigate();
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Operate"
        title="Customers"
        sub={`${total.toLocaleString()} customers${filter === 'all' ? '' : ` · ${filter}`}.`}
        actions={
          <ToolBtn icon="download" onClick={() => void handleExport()}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </ToolBtn>
        }
      />
      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load customers: {(error as Error).message}
        </div>
      ) : null}
      {exportError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Export failed: {exportError}
        </div>
      ) : null}
      <Toolbar>
        <ToolSearch value={q} onChange={setQ} placeholder="Search email, first or last name…" />
        {FILTERS.map((f) => (
          <ToolChip key={f.key} active={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label}
          </ToolChip>
        ))}
      </Toolbar>
      <DataTable
        columns={COLUMNS}
        rows={rows}
        onRowClick={(r) => void navigate({ to: `/customers/${r.id}` as never })}
        emptyLabel={isLoading ? 'Loading…' : 'No matching customers.'}
        pagination={{ page, limit, total, onPageChange: setPage, onLimitChange: setLimit }}
      />
    </div>
  );
}
